import React, { useEffect, useRef, useState } from 'react';

interface UserMenuProps {
  name?: string;
  email?: string;
  roles?: string[];
  onLogout: () => void;
}

/** Keeps role membership out of the header until asked for. */
const UserMenu: React.FC<UserMenuProps> = ({ name, email, roles, onLogout }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocumentClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocumentClick);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onDocumentClick);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  return (
    <div className="user-menu" ref={containerRef}>
      <button
        type="button"
        className="user-menu-trigger"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen(!open)}
      >
        <span className="user-menu-name">{name || 'Signed in'}</span>
        <span className="user-menu-caret" aria-hidden="true">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="user-menu-panel" role="menu">
          <div className="user-menu-identity">
            <strong>{name}</strong>
            {email && <span className="user-menu-email">{email}</span>}
          </div>
          <div className="user-menu-section">
            <span className="user-menu-label">Group membership</span>
            {roles && roles.length > 0 ? (
              <ul className="user-menu-roles">
                {roles.map((role) => (
                  <li key={role}>{role}</li>
                ))}
              </ul>
            ) : (
              <span className="user-menu-email">No roles assigned</span>
            )}
          </div>
          <button type="button" className="btn btn-sm user-menu-logout" onClick={onLogout}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
